import { CalendarClock, CheckCircle2, CircleX, Clock, Coffee, LucideIcon, Check, Monitor, Phone, Projector, Wifi } from 'lucide-react';
import { BookingFeature } from 'app/data/types/booking';

/** Which status "family" a raw resource-booking status maps to (drives icon + copy). `neutral` is the
 *  fallback for terminal/unknown statuses — NOT `requested`, which is a meaningful pending state. */
export type BookingStatusView = 'requested' | 'confirmed' | 'cancelled' | 'neutral';

const CANCELLED_RE = /cancel|denied|no[_-]?show/i;
// Confirmed AND done/positive-terminal statuses share the confirmed view (a completed booking must not
// read as "requested"). Note: cancelled is checked first, so `denied` never reaches here.
const CONFIRMED_RE = /accept|confirm|booked|check(ed)?[_-]?in|complete|attend|resolv/i;
const REQUESTED_RE = /pending|request|wait/i;

export const bookingStatusView = (status: string | undefined): BookingStatusView => {
  if (!status) return 'neutral';
  if (CANCELLED_RE.test(status)) return 'cancelled';
  if (CONFIRMED_RE.test(status)) return 'confirmed';
  if (REQUESTED_RE.test(status)) return 'requested';
  return 'neutral';
};

interface StatusMeta {
  icon: LucideIcon;
  iconClassName: string;
  titleId: string;
  descriptionId: string;
}

export const BOOKING_STATUS_META: Record<BookingStatusView, StatusMeta> = {
  requested: {
    icon: Clock,
    iconClassName: 'text-system-orange',
    titleId: 'booking.detail.status.requested.title',
    descriptionId: 'booking.detail.status.requested.body',
  },
  confirmed: {
    icon: CheckCircle2,
    iconClassName: 'text-system-green',
    titleId: 'booking.detail.status.confirmed.title',
    descriptionId: 'booking.detail.status.confirmed.body',
  },
  cancelled: {
    icon: CircleX,
    iconClassName: 'text-system-pink',
    titleId: 'booking.detail.status.cancelled.title',
    descriptionId: 'booking.detail.status.cancelled.body',
  },
  neutral: {
    icon: CalendarClock,
    iconClassName: 'text-system-grey-dark',
    titleId: 'booking.detail.status.neutral.title',
    descriptionId: 'booking.detail.status.neutral.body',
  },
};

/** Which status "family" a raw event (services_booking) status maps to. Events use a distinct
 *  vocabulary from room bookings: registered / waitlisted / cancelled (+ neutral fallback). */
export type EventStatusView = 'registered' | 'waitlisted' | 'cancelled' | 'neutral';

const WAITLIST_RE = /wait/i;
const REGISTERED_RE = /confirm|booked|check(ed)?[_-]?in|attend|complete|register/i;

export const eventStatusView = (status: string | undefined): EventStatusView => {
  if (!status) return 'neutral';
  if (CANCELLED_RE.test(status)) return 'cancelled';
  if (WAITLIST_RE.test(status)) return 'waitlisted';
  if (REGISTERED_RE.test(status)) return 'registered';
  return 'neutral';
};

export const EVENT_STATUS_META: Record<EventStatusView, StatusMeta> = {
  registered: {
    icon: CheckCircle2,
    iconClassName: 'text-system-green',
    titleId: 'booking.detail.event.status.registered.title',
    descriptionId: 'booking.detail.event.status.registered.body',
  },
  waitlisted: {
    icon: Clock,
    iconClassName: 'text-system-orange',
    titleId: 'booking.detail.event.status.waitlisted.title',
    descriptionId: 'booking.detail.event.status.waitlisted.body',
  },
  cancelled: {
    icon: CircleX,
    iconClassName: 'text-system-pink',
    titleId: 'booking.detail.event.status.cancelled.title',
    descriptionId: 'booking.detail.event.status.cancelled.body',
  },
  neutral: {
    icon: CalendarClock,
    iconClassName: 'text-system-grey-dark',
    titleId: 'booking.detail.event.status.neutral.title',
    descriptionId: 'booking.detail.event.status.neutral.body',
  },
};

/** Room feature `default_type` → icon (fallback: a generic check). */
const AMENITY_ICON: Record<string, LucideIcon> = {
  TV: Monitor,
  PROJECTOR: Projector,
  WIFI: Wifi,
  PHONE: Phone,
  COFFEE: Coffee,
};

export const amenityIcon = (feature: BookingFeature): LucideIcon =>
  AMENITY_ICON[(feature.default_type ?? '').toUpperCase()] ?? Check;
